import React, {forwardRef, Ref, useCallback, useMemo} from "react";
import {MuiRFF} from "../../form";
import {FormControl, FormHelperText, TextFieldProps, Theme} from "@mui/material";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import useIsMounted from "../../../hooks/use-is-mounted";
import './index.scss';
import {Field} from "react-final-form";
import classNames from "classnames";
import UIUtils from "../../../utils";
import {configureAdminFileUploadAdapter} from "./admin-upload-adapter";


type MuiCKEditorTextFieldProps = Omit<TextFieldProps, 'onChange' | 'onBlur' | 'value' | 'type'> & {
    form: false;
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: (value: string) => void;
}

type FormCKEditorTextFieldProps = Omit<MuiRFF.TextFieldProps, 'onChange' | 'onBlur' | 'value' | 'type'> & {
    form: true;
}

type CKEditorTextFieldTypes = "MuiRFF" | "Mui";


export type CKEditorTextFieldProps<T extends CKEditorTextFieldTypes = "Mui"> =
    T extends "MuiRFF"
        ? FormCKEditorTextFieldProps
        : T extends "Mui"
            ? MuiCKEditorTextFieldProps
            : never;

const _CKEditorTextfield = <T extends CKEditorTextFieldTypes = "Mui">(props: CKEditorTextFieldProps<T>, ref: Ref<CKEditor<ClassicEditor>>) => {
    const isMounted = useIsMounted();
    const editorConfig = useMemo(() => ({language: {ui: 'en', content: 'en'}}), []);

    /**
     * Configures the admin file upload adapter on the editor instance when it's ready.
     */
    const handleEditorReady = useCallback((editor: ClassicEditor) => {
        if (!isMounted())
            return;
        configureAdminFileUploadAdapter(editor);
    }, [isMounted]);

    /**
     * The form control sx.
     */
    const formControlSx = useCallback<ThemeMixin<Theme>>((theme) => ({
        width: '100%',
        ...UIUtils.invokeSx(props.sx, theme) as any,
    }), [props.sx])

    return (
        <>
            {
                !props.form &&
                <FormControl sx={formControlSx} error={props.error} required={props.required}
                             className={classNames({'error': props.error})}>
                    <CKEditor
                        ref={ref}
                        data={props?.value ?? ""}
                        disabled={props.disabled}
                        editor={ClassicEditor}
                        config={editorConfig}
                        onReady={handleEditorReady}
                        onFocus={(event, editor) => handleEditorReady(editor)}
                        onChange={(event, editor) => isMounted() && props.onChange?.(editor.getData())}
                        onBlur={(event, editor) => isMounted() && props.onBlur?.(editor.getData())}
                    />
                    {
                        props.helperText &&
                        <FormHelperText error={props.error}>
                            {props.helperText}
                        </FormHelperText>
                    }
                </FormControl>
            }
            {
                props.form &&
                <Field
                    name={props.name}
                    render={({input, meta}) => {
                        const {error, submitError} = meta;
                        const isError = MuiRFF.showErrorOnChange({meta});
                        return (
                            <FormControl
                                sx={formControlSx}
                                className={classNames({'error': isError})}
                                error={isError}
                                required={props.required}
                            >
                                <CKEditor
                                    ref={ref}
                                    data={input.value}
                                    disabled={props.disabled}
                                    editor={ClassicEditor}
                                    config={editorConfig}
                                    onReady={handleEditorReady}
                                    onFocus={(event, editor) => handleEditorReady(editor)}
                                    onChange={(event, editor) => {
                                        if (!isMounted())
                                            return
                                        input.onChange(editor.getData());
                                    }}
                                    onBlur={(event, editor) => {
                                        if (!isMounted())
                                            return
                                        // @ts-ignore
                                        input.onBlur(editor.getData())
                                    }}
                                />
                                {
                                    (isError || !!props.helperText) &&
                                    <FormHelperText error={isError}>
                                        {error || submitError || props.helperText}
                                    </FormHelperText>
                                }
                            </FormControl>
                        )
                    }}
                />
            }
        </>
    )
}

const CKEditorTextfield = forwardRef(_CKEditorTextfield)

export default CKEditorTextfield;
